/**
 * Timelapse recorder for the history player.
 *
 * Captures the world <canvas> (the PIXI WebGL view) frame-by-frame while the
 * history playback engine advances, and produces a downloadable clip.
 *
 *   • WebM  — primary path. `output.captureStream()` + `MediaRecorder`. No deps.
 *   • GIF   — fallback for browsers without MediaRecorder/captureStream
 *             (notably iOS Safari). gif.js is loaded on demand from a CDN — the
 *             same runtime-script pattern the analytics facade uses — so it adds
 *             nothing to the bundle and needs no npm install / worker file.
 *
 * The source PIXI canvas is blitted into our own fixed-resolution 2D output
 * canvas each frame; the recorder samples that output canvas. This keeps the
 * exported resolution independent of the device screen and gives us a surface
 * to burn a watermark onto.
 *
 * HTML overlays are not part of a canvas stream, so anything drawn over the
 * world in the DOM has to be re-drawn here to survive the export. Speech
 * bubbles are: the caller passes an `overlay` hook, invoked after every blit
 * (see `speechBubbleCanvas.ts`). The HUD deliberately is not — a recording is
 * of the world, not of the interface around it.
 */

// gif.js is untyped; treat its constructor/instances as `any`.
const GIF_JS_URL     = 'https://cdn.jsdelivr.net/npm/gif.js@0.2.0/dist/gif.js';
const GIF_WORKER_URL = 'https://cdn.jsdelivr.net/npm/gif.js@0.2.0/dist/gif.worker.js';

export type TimelapseFormat = 'webm' | 'gif';

/**
 * Watermark scaffold. Today it renders text (the brand has no logo image asset);
 * this is the deliberate swap-in point where a real logo image can later replace
 * the text render without touching the recording pipeline.
 */
export interface WatermarkConfig {
  wordmark: string; // e.g. "WE ARE PIXELS"
  caption:  string; // e.g. "wearepixels.ru"
}

export interface TimelapseRecorderOptions {
  /** Source PIXI canvas to capture. Must be created with preserveDrawingBuffer. */
  source:      HTMLCanvasElement;
  /** Capture frame rate. Default 30 (webm) / 12 (gif). */
  fps?:        number;
  /** Output height cap in px (width follows source aspect). Default 720. */
  maxHeight?:  number;
  /** Force a format. Default: webm when supported, else gif. */
  format?:     TimelapseFormat;
  /** Watermark to burn in, or null for a clean export. */
  watermark?:  WatermarkConfig | null;
  /** GIF encode progress 0..1 (gif path only). */
  onProgress?: (p: number) => void;
  /**
   * Drawn over every captured frame, in output-canvas pixels. This is how DOM
   * overlays (speech bubbles) get into the clip at all — without it the export
   * shows the world but none of what the pixels said.
   */
  overlay?:    (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
}

export interface TimelapseRecorder {
  readonly format: TimelapseFormat;
  /** Begin capturing. */
  start(): void;
  /** Blit the current source frame into the output surface. Call every RAF. */
  blitFrame(): void;
  /** Stop and resolve the encoded clip. */
  stop(): Promise<Blob>;
  /** Abort without producing a blob. */
  cancel(): void;
  /** Suggested download file extension (without dot). */
  readonly ext: string;
}

// ── feature detection ─────────────────────────────────────────────────

const WEBM_MIMES = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];

function pickWebmMime(): string | null {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return null;
  for (const m of WEBM_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export function canRecordWebM(): boolean {
  return typeof MediaRecorder !== 'undefined'
    && typeof HTMLCanvasElement.prototype.captureStream === 'function'
    && pickWebmMime() !== null;
}

// ── gif.js on-demand loader ───────────────────────────────────────────

let gifPromise: Promise<any> | null = null;

function ensureGifJs(): Promise<any> {
  const w = window as any;
  if (w.GIF) return Promise.resolve(w.GIF);
  if (gifPromise) return gifPromise;
  gifPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GIF_JS_URL;
    s.async = true;
    s.onload = () => (w.GIF ? resolve(w.GIF) : reject(new Error('gifjs_missing')));
    s.onerror = () => { gifPromise = null; reject(new Error('gifjs_load_failed')); };
    document.head.appendChild(s);
  });
  return gifPromise;
}

// ── helpers ───────────────────────────────────────────────────────────

/** Render the watermark into the bottom-right of the output canvas. */
function drawWatermark(ctx: CanvasRenderingContext2D, w: number, h: number, wm: WatermarkConfig) {
  const pad = Math.round(h * 0.03);
  const fs  = Math.max(11, Math.round(h * 0.038));
  ctx.save();
  ctx.textAlign    = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.shadowColor  = 'rgba(0,0,0,0.85)';
  ctx.shadowBlur   = Math.round(fs * 0.5);
  // Rendered in white (not the dark brand palette #0E0C0C) so it stays legible
  // over the dark world canvas.
  ctx.fillStyle    = '#ffffff';
  ctx.globalAlpha  = 0.88;
  ctx.font         = `700 ${fs}px 'Courier New', monospace`;
  ctx.fillText(wm.wordmark, w - pad, h - pad - Math.round(fs * 0.85));
  ctx.globalAlpha  = 0.68;
  ctx.font         = `${Math.round(fs * 0.62)}px 'Courier New', monospace`;
  ctx.fillText(wm.caption, w - pad, h - pad);
  ctx.restore();
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export { downloadBlob };

// Guard against runaway memory on the GIF path (each frame is kept in RAM).
const GIF_MAX_FRAMES = 400;

// ── factory ───────────────────────────────────────────────────────────

export async function createTimelapseRecorder(
  opts: TimelapseRecorderOptions,
): Promise<TimelapseRecorder> {
  const source    = opts.source;
  const format: TimelapseFormat = opts.format ?? (canRecordWebM() ? 'webm' : 'gif');
  const fps       = opts.fps ?? (format === 'webm' ? 30 : 12);
  const maxHeight = opts.maxHeight ?? 720;
  const watermark = opts.watermark ?? null;

  // Output surface sized to source aspect, capped, rounded to even dims.
  const srcW = source.width  || 1;
  const srcH = source.height || 1;
  const aspect = srcW / srcH;
  let outH = Math.min(maxHeight, srcH);
  let outW = Math.round(outH * aspect);
  outW += outW % 2;
  outH += outH % 2;

  const output = document.createElement('canvas');
  output.width  = outW;
  output.height = outH;
  const ctx = output.getContext('2d')!;

  const overlay = opts.overlay ?? null;

  const blit = () => {
    ctx.drawImage(source, 0, 0, srcW, srcH, 0, 0, outW, outH);
    // Overlay before the watermark, so the watermark always stays on top.
    if (overlay) {
      // A throwing overlay must not kill the recording in progress: the clip
      // is worth more than one missing bubble.
      try { overlay(ctx, outW, outH); } catch { /* keep recording */ }
    }
    if (watermark) drawWatermark(ctx, outW, outH, watermark);
  };

  if (format === 'webm') {
    const mime   = pickWebmMime()!;
    const stream = output.captureStream(fps);
    const rec    = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 8_000_000 });
    const chunks: BlobPart[] = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };

    return {
      format,
      ext: 'webm',
      start() { blit(); rec.start(); },
      blitFrame() { blit(); },
      stop() {
        return new Promise<Blob>((resolve) => {
          rec.onstop = () => resolve(new Blob(chunks, { type: mime }));
          try { rec.requestData(); } catch { /* not supported everywhere */ }
          rec.stop();
        });
      },
      cancel() { try { rec.stop(); } catch { /* ignore */ } },
    };
  }

  // GIF path
  const GIF = await ensureGifJs();
  let gif: any = null;
  let frames = 0;
  let lastCapture = 0;
  const frameInterval = 1000 / fps;

  return {
    format,
    ext: 'gif',
    start() {
      gif = new GIF({
        workers: 2,
        quality: 10,
        width: outW,
        height: outH,
        workerScript: GIF_WORKER_URL,
        background: '#000000',
      });
      frames = 0;
      lastCapture = 0;
      blit();
    },
    blitFrame() {
      blit();
      const now = performance.now();
      if (frames >= GIF_MAX_FRAMES) return;
      if (now - lastCapture < frameInterval) return;
      lastCapture = now;
      frames++;
      // copy:true snapshots the pixels now; delay in ms per frame.
      gif.addFrame(ctx, { copy: true, delay: Math.round(frameInterval) });
    },
    stop() {
      return new Promise<Blob>((resolve, reject) => {
        if (!gif || frames === 0) { reject(new Error('no_frames')); return; }
        gif.on('progress', (p: number) => opts.onProgress?.(p));
        gif.on('finished', (blob: Blob) => resolve(blob));
        gif.render();
      });
    },
    cancel() { try { gif?.abort(); } catch { /* ignore */ } },
  };
}
